import { Children, cloneElement, isValidElement } from 'react'
import {
  Route,
  NavLink
} from 'react-router-dom'
import { withRouter } from 'react-router'

/**
 * Utility that comparing `hash` from location with fixed `hash` in `HashRoute`.
 * `defaultRoot` props here acting as a flag for `Index Route` under condition that `hash` is not specified.
 * https://stackblitz.com/edit/hashroute-example?file=HashRoute.js
 */
export const matchHash = ({ location, hash, path, defaultRoot = false }) => {
  const locationHash = location.hash
  // pathname + hash to prevent duoble match for hash route
  const locationPath = location.pathname
  const hashMatches = locationHash === hash

  let matchDisplay = false

  // disable it for it will cause bug because server side cannot get hash from url
  // https://stackoverflow.com/questions/3664257/why-is-the-hash-part-of-the-url-not-available-on-the-server-side
  if (global.window && defaultRoot && !locationHash) {
    matchDisplay = true
  } else if (hash) {
    matchDisplay = hashMatches
  }
  // 如果有path則一併比對path，通常只有HashNavLink才會給Path
  if (!matchDisplay && path && locationPath === path) {
    matchDisplay = true
  }
  // console.log(location, hash, path, hashMatches, matchDisplay, locationPath, path)
  return matchDisplay
}

/**
 * Route for matching a single hash and rendering.
 * Must declare `hash` or `defaultRoot` as one of your props, or it won't work.
 * It works with or without `HashSwitch`.
 * When using with `HashSwitch`, `matchSwitch` will be injected as flag to display component.
 */
export const HashRoute = ({ component: Component, matchSwitch, defaultRoot, hash, ...routeProps }) => (
  <Route
    render={({ location, ...props }) => {
      if (!defaultRoot && !hash) {
        throw new Error('Warning: Neither `defaultRoot` nor `hash` is specified!')
      }
      const matchDisplay = matchSwitch || matchHash({ location, defaultRoot, hash })
      return matchDisplay && (
        <Component
          location={location}
          {...props}
          {...routeProps}
        />
      )
    }}
  />
)

/**
 * @Deprecatable? or not?
 *
 * Route that works with `HashSwitch`
 * Same usage as `HashRoute` but it is not attached with the matching algorithm.
 */
export const HashSwitchRoute = ({ component: Component, defaultRoot, hash, ...props }) => {
  return <Component {...props} />
}

/**
 * Display preset error component for `HashSwitchRoute`
 */
export const HashRouteCatch = ({ ...props }) => {
  return <HashSwitchRoute {...props} />
}

/**
 * Switch for `HashRoute`, it is recommended using with `HashSwitchRoute` to have better performance.
 * It catches undefined hash and render component that being set in `HashRouteCatch` if any, or empty content.
 *
 * The differnces of using `HashSwitch` or just `HashRoute` is that React-router parsing each of `HashRoute` regardless it matches or not.
 * By applying `HashSwitch`, it reduces the time of parsing and enhanced with error page redirecting.
 */
export const HashSwitchBase = ({ children, match, location, history, ...props }) => {
  const matchSwitch = false
  let child
  let errorElement = null
  Children.forEach(children, element => {
    if (!isValidElement(element) || child) return

    const { defaultRoot, hash } = element.props
    if (element.type.name === 'HashRouteCatch') {
      errorElement = element
      return
    }
    if (!defaultRoot && !hash) {
      throw new Error('Warning: Neither `defaultRoot` nor `hash` is specified!')
    }

    const matchDisplay = matchHash({ location, defaultRoot, hash })
    if (matchDisplay) {
      child = element
    }
  })

  return child ? cloneElement(child, { location, match, history, matchSwitch }) : errorElement
}

/**
 * With withRouter wrapper to inject location and match props from Recat-router.
*/
export const HashSwitch = withRouter(HashSwitchBase)

export const HashNavLink = ({ hash, path, defaultRoot, isActive: getIsActive, ...rest }) => {
  const defaultGetIsActive = (match, location) => matchHash({ location, hash, defaultRoot })
  const isActive = getIsActive || defaultGetIsActive
  return (
    <NavLink
      {...rest}
      isActive={isActive}
    />
  )
}
